import { useTranslate, type Identifier } from "ra-core";
import { AlertTriangle, CheckCircle2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import { ProgressBar } from "../../design/ProgressBar";
import { useContactExpedienteMissing } from "./useContactExpediente";

/** The light-red band: icon plus text, never colour alone. */
const MissingList = ({ labels }: { labels: string[] }) => {
  const translate = useTranslate();
  return (
    <div className="rounded-md bg-brand-red-bg p-4 text-brand-red-text">
      <p className="flex items-center gap-2 text-[15px] font-semibold">
        <AlertTriangle className="size-5 shrink-0" aria-hidden="true" />
        {translate("crm.contact_expediente.missing_title")}
      </p>
      <ul className="mt-2 list-disc space-y-1 pl-9 text-[15px]">
        {labels.map((label) => (
          <li key={label}>{label}</li>
        ))}
      </ul>
    </div>
  );
};

/**
 * Header of the Expediente tab: one navy "n de m" bar and, first, what is missing on a light-red
 * band (or a calm "completo" line). Answers "que falta y quien lo sube".
 */
export const ExpedienteChecklist = ({
  done,
  total,
  missing,
}: {
  done: number;
  total: number;
  missing: string[];
}) => {
  const translate = useTranslate();
  const progress = translate("crm.contact_expediente.progress", {
    done,
    total,
  });
  return (
    <header className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <ProgressBar
          value={done}
          max={Math.max(total, 1)}
          label={translate("crm.contact_expediente.progress_label")}
          className="flex-1"
        />
        <span className="text-[15px] font-semibold tabular-nums">
          {progress}
        </span>
      </div>
      {missing.length > 0 ? (
        <MissingList labels={missing} />
      ) : (
        <p className="flex items-center gap-2 rounded-md bg-brand-green-bg p-4 text-[15px] font-medium text-brand-green-text">
          <CheckCircle2 className="size-5 shrink-0" aria-hidden="true" />
          {translate("crm.contact_expediente.complete_title")}
        </p>
      )}
      <p className="text-[15px] text-muted-foreground">
        {translate("crm.contact_expediente.who_uploads")}
      </p>
    </header>
  );
};

/**
 * What every role outside the five audiences sees: ONLY the read-only list of missing labels and
 * the note saying who captures the file. No value, no form, and nothing throws on a refusal.
 */
export const ExpedienteRestrictedView = ({
  contactId,
}: {
  contactId: Identifier;
}) => {
  const translate = useTranslate();
  const query = useContactExpedienteMissing(contactId);
  return (
    <section className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold">
        {translate("crm.contact_expediente.restricted_title")}
      </h2>
      {query.isPending ? (
        <div role="status" aria-busy="true" className="space-y-2">
          <span className="sr-only">
            {translate("crm.contact_expediente.loading")}
          </span>
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-6 w-2/3" />
        </div>
      ) : query.isError ? (
        <div className="flex flex-col items-start gap-3">
          <p role="alert" className="text-[15px]">
            {translate("crm.contact_expediente.restricted_error")}
          </p>
          <Button
            type="button"
            variant="outline"
            className="h-11"
            onClick={query.retry}
          >
            {translate("crm.contact_expediente.retry")}
          </Button>
        </div>
      ) : query.data.length > 0 ? (
        <MissingList labels={query.data} />
      ) : (
        <p className="flex items-center gap-2 text-[15px]">
          <CheckCircle2
            className="size-5 shrink-0 text-brand-green-text"
            aria-hidden="true"
          />
          {translate("crm.contact_expediente.restricted_empty")}
        </p>
      )}
      <p className="text-[15px] text-muted-foreground">
        {translate("crm.contact_expediente.restricted_note")}
      </p>
    </section>
  );
};
